import { NavigationError } from '@angular/router';

const CHUNK_RELOAD_STORAGE_KEY = 'app_lazy_chunk_reload_at';
const CHUNK_RELOAD_COOLDOWN_MS = 60_000;
const CACHE_BUST_PARAM = '_v';

const STALE_CHUNK_PATTERNS = [
  'Failed to fetch dynamically imported module',
  'Importing a module script failed',
  'error loading dynamically imported module',
  'Loading chunk',
  'ChunkLoadError',
];

export function isStaleLazyChunkError(error: unknown): boolean {
  if (!error) {
    return false;
  }

  if (error instanceof Error && error.name === 'ChunkLoadError') {
    return true;
  }

  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
      ? error
      : String(error);

  return STALE_CHUNK_PATTERNS.some((pattern) => message.includes(pattern));
}

/**
 * Reload at most once per cooldown when a lazy chunk 404s after deploy / dev rebuild.
 * Returns true when a reload was triggered.
 */
export function reloadOnceForStaleLazyChunk(
  navigate: (url: string) => void = (url) => window.location.replace(url),
  now: number = Date.now()
): boolean {
  try {
    const lastReloadAt = Number(
      sessionStorage.getItem(CHUNK_RELOAD_STORAGE_KEY)
    );
    if (lastReloadAt && now - lastReloadAt < CHUNK_RELOAD_COOLDOWN_MS) {
      return false;
    }
    sessionStorage.setItem(CHUNK_RELOAD_STORAGE_KEY, String(now));
  } catch {
    // sessionStorage unavailable — still attempt reload
  }

  const url = new URL(window.location.href);
  url.searchParams.set(CACHE_BUST_PARAM, String(now));
  navigate(url.toString());
  return true;
}

export function removeCacheBustParam(): void {
  const url = new URL(window.location.href);
  if (!url.searchParams.has(CACHE_BUST_PARAM)) {
    return;
  }

  url.searchParams.delete(CACHE_BUST_PARAM);
  window.history.replaceState(window.history.state, '', url.toString());
}

export function handleStaleLazyChunkNavigationError(
  navigationError: NavigationError
): void {
  if (!isStaleLazyChunkError(navigationError.error)) {
    throw navigationError;
  }

  reloadOnceForStaleLazyChunk();
}

export function registerStaleLazyChunkRecovery(): void {
  window.addEventListener('unhandledrejection', (event) => {
    if (!isStaleLazyChunkError(event.reason)) {
      return;
    }

    event.preventDefault();
    reloadOnceForStaleLazyChunk();
  });
}
